import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, DataTable, Badge, Skeleton, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Filters, Pagination } from '../../components/uiChrome.jsx';
import { formatRelativeTime, CHIP_BASE, CHIP_LABEL, CHIP_TONE } from '../../utils';
import { downloadTextFile } from '../../../../shared/utils/downloadTextFile';
import { useInstanceLogs } from '../../hooks/instance';
import { AlertTriangle, ArrowRight } from 'lucide-react';

// Prototype `.audit-ev` chip: outlined Badge — tinted fill, colored border, mono
// uppercase label. Base + tone come from the shared chip tokens; uppercase +
// tracking are this surface's own modifiers. success → accent blue; else → red.
const STATUS_CHIP_BASE = `${CHIP_BASE} !uppercase !tracking-[0.04em]`;
const statusTone = (status) => (status === 'success' ? 'accent' : 'danger');
const STATUS_OPTIONS = ['success', 'failed', 'timeout', 'rate_limited'];
const STUDIO_OPTIONS = ['agentic', 'workforce', 'governance'];
const DATE_OPTIONS = ['1h', '24h', '7d', '30d', 'all'];

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
const DATE_RANGE_MS = { '1h': HOUR, '24h': DAY, '7d': 7 * DAY, '30d': 30 * DAY };
const DEFAULT_PAGE_SIZE = 10;

/**
 * Instance Detail — Logs tab (CT-4189, admin-only, default tab basic per
 * prototype). Mirrors the prototype `instanceLogsHTML`: Date / Status / Studio
 * dropdowns + actor-or-action search over a workspace `DataTable` (Timestamp ·
 * Status · Action · Actor · Studio · Latency, with the error inline under the
 * latency). Sticky `Pagination`; CSV export of the current filtered set. Newest
 * first — the reference `now` comes from the hook so the date cutoff is stable.
 */
const LogsTab = () => {
  const { t } = useTranslation('admin-studio');
  const { instance } = useOutletContext();
  const { rows, now, isLoading, error } = useInstanceLogs(instance.instanceId);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState([]);
  const [studio, setStudio] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);

  const searchProps = useMemo(
    () => ({
      value: search,
      onChange: (e) => {
        setSearch(e?.target ? e.target.value : e);
        setPage(1);
      },
      onClear: () => {
        setSearch('');
        setPage(1);
      },
      placeholder: t('integrations.instance.logs.searchPlaceholder'),
    }),
    [search, t],
  );

  const filtersConfig = useMemo(
    () => [
      {
        id: 'date',
        label: t('integrations.instance.logs.dateLabel'),
        multiple: false,
        value: dateRange,
        onChange: (next) => {
          setDateRange(next);
          setPage(1);
        },
        options: DATE_OPTIONS.map((v) => ({ value: v, label: t(`integrations.instance.logs.date.${v}`) })),
      },
      {
        id: 'status',
        label: t('integrations.instance.logs.statusFilter.all'),
        multiple: false,
        value: status,
        onChange: (next) => {
          setStatus(next);
          setPage(1);
        },
        options: STATUS_OPTIONS.map((v) => ({
          value: v,
          label: t(`integrations.instance.logs.statusFilter.${v}`),
        })),
      },
      {
        id: 'studio',
        label: t('integrations.instance.logs.studioFilter.all'),
        multiple: false,
        value: studio,
        onChange: (next) => {
          setStudio(next);
          setPage(1);
        },
        options: STUDIO_OPTIONS.map((v) => ({
          value: v,
          label: t(`integrations.instance.logs.studioFilter.${v}`),
        })),
      },
    ],
    [dateRange, status, studio, t],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const range = dateRange[0];
    const cutoff = range && range !== 'all' ? now - DATE_RANGE_MS[range] : null;
    const statusValue = status[0];
    const studioValue = studio[0];
    return rows.filter((r) => {
      if (cutoff != null && new Date(r.timestamp).getTime() < cutoff) return false;
      if (statusValue && r.status !== statusValue) return false;
      if (studioValue && r.studio !== studioValue) return false;
      if (q && !`${r.action} ${r.actor}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, now, search, status, studio, dateRange]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * perPage;
  const pageRows = filtered.slice(pageStart, pageStart + perPage);
  const rangeLabel =
    filtered.length === 0 ? '0' : `${pageStart + 1}–${Math.min(pageStart + perPage, filtered.length)}`;

  const handleExportCsv = () => {
    // Quote any field that could contain the delimiter so a comma in an error
    // message (e.g. "Conflict, resource changed") can't shift the columns.
    const cell = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = 'timestamp,status,action,actor,studio,latency_ms,error';
    const body = filtered.map((r) =>
      [r.timestamp, r.status, r.action, r.actor, r.studio, r.latency_ms, r.error].map(cell).join(','),
    );
    downloadTextFile(`${instance.instanceId}-logs.csv`, [header, ...body].join('\n'));
  };

  const columns = useMemo(
    () => [
      {
        key: 'timestamp',
        label: t('integrations.instance.logs.columns.timestamp'),
        headerClassName: 'w-[132px]',
        render: (v) => (
          <span className="text-[13px] text-content-secondary" title={new Date(v).toLocaleString()}>
            {formatRelativeTime(v, t, now || undefined)}
          </span>
        ),
      },
      {
        key: 'status',
        label: t('integrations.instance.logs.columns.status'),
        headerClassName: 'w-[128px]',
        render: (v) => (
          <Badge
            variant="neutral"
            size="small"
            className={`${STATUS_CHIP_BASE} ${CHIP_TONE[statusTone(v)]}`}
          >
            <span className={CHIP_LABEL}>{t(`integrations.instance.logs.status.${v}`)}</span>
          </Badge>
        ),
      },
      {
        key: 'action',
        label: t('integrations.instance.logs.columns.action'),
        render: (v) => (
          <code className="rounded-md bg-surface-muted px-1.5 py-0.5 font-mono text-[11px] text-content">
            {v}
          </code>
        ),
      },
      { key: 'actor', label: t('integrations.instance.logs.columns.actor') },
      {
        key: 'studio',
        label: t('integrations.instance.logs.columns.studio'),
        headerClassName: 'w-[120px]',
        render: (v) => t(`integrations.instance.logs.studioFilter.${v}`),
      },
      {
        key: 'latency_ms',
        label: t('integrations.instance.logs.columns.latency'),
        headerClassName: 'w-[160px]',
        render: (v, row) => {
          const failed = row.status !== 'success';
          return (
            <span className={`font-mono text-[11px] ${failed ? 'text-red-500' : 'text-content'}`}>
              {v}ms
              {row.error ? <span className="mt-0.5 block text-[10px] leading-snug text-red-500">{row.error}</span> : null}
            </span>
          );
        },
      },
    ],
    [t, now],
  );

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (error) return <StatusMessage icon={AlertTriangle} title={t('common.requestFailed')} subtitle={error} />;

  return (
    <Card>
      <Card.Body className="flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-content">{t('integrations.instance.logs.title')}</h3>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
            <span className="text-content-muted">{t('integrations.instance.logs.scope')}</span>
            <button
              type="button"
              onClick={handleExportCsv}
              className="inline-flex items-center gap-1 font-medium text-[#2173FF] hover:underline"
            >
              {t('integrations.instance.logs.export')}
              <ArrowRight size={13} aria-hidden="true" />
            </button>
          </div>
        </div>

        <Filters
          searchProps={searchProps}
          filters={filtersConfig}
          showAllFilters={false}
          maxFilters={3}
          onClearAll={() => {
            setSearch('');
            setStatus([]);
            setStudio([]);
            setDateRange([]);
            setPage(1);
          }}
        />

        <DataTable
          variant="workspace"
          columns={columns}
          data={pageRows}
          minTableWidth={880}
          emptyMessage={
            rows.length === 0
              ? t('integrations.instance.logs.emptyNone')
              : t('integrations.instance.logs.empty')
          }
        />

        <Pagination
          rangeLabel={rangeLabel}
          totalItems={filtered.length}
          currentPage={safePage}
          totalPages={totalPages}
          perPage={perPage}
          hasPrevious={safePage > 1}
          hasNext={safePage < totalPages}
          onPrevPage={() => setPage(Math.max(1, safePage - 1))}
          onNextPage={() => setPage(Math.min(totalPages, safePage + 1))}
          onPageSizeChange={(size) => {
            setPerPage(size);
            setPage(1);
          }}
        />
      </Card.Body>
    </Card>
  );
};

export default LogsTab;
